import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

import { receiptsSelectors } from 'app-components/flux/receipts';
import type { FeatureFlagsKeys } from 'app-components/flux/feature-flags/featureFlagsTypes';
import type { RootState } from 'store';
import * as navIcons from './navIcons';

export const taxPlannerPath = '/tax-planner';
export const upcomingNavKey = 'upcoming';
export const mileageTrackerPath = '/mileage-tracker';
export const receiptCenterPath = '/receipt-center';
const dashboardNavKey = '';

type NavGroup = 'Business' | 'Planning' | 'Insights';

export const navOrderV2 = [
  dashboardNavKey,
  'transactions',
  'spending',
  'net-worth',
  ['Business'],
  'clients',
  'invoices',
  'sales-and-billing',
  receiptCenterPath.replace(/^\//, ''),
  mileageTrackerPath.replace(/^\//, ''),
  taxPlannerPath.replace(/^\//, ''),
  ['Planning'],
  'spending-plan',
  'goals',
  upcomingNavKey,
  'planning-tools',
  ['Insights'],
  'investing',
  'watchlist',
  'saved-reports',
  'reports',
  'miles',
] as const satisfies readonly (string | readonly NavGroup[])[];

export type FeaturePage = {
  /** title used in the QTip and used for the automation ID */
  title: string;
  /** Do we show the 'New' chip next to the name? */
  newChip?: boolean;
  /** if in navbar, which section is it in */
  nav: 'main' | 'sub';
  /** alt text for the icon, used for accessibility and automation ID */
  alt?: string;
  /** Navbar icon */
  icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  /** feature flag name if feature is to be shown based on flag */
  featureFlag?: FeatureFlagsKeys;
  /** feature flag name if feature is to be hidden based on flag */
  featureFlagHide?: FeatureFlagsKeys;
  /** optional shared selector for feature-specific access rules */
  accessSelector?: (state: RootState) => boolean;
  path: string;
  businessEnabled?: boolean;
  popover?: {
    id: string;
    default: string;
    pages: Array<{ type: 'item' | 'divider'; label: string; tabName: string; featureFlag?: FeatureFlagsKeys }>;
  };
  guideId?: string;
};

const defaultPage = {
  title: '',
  nav: 'main',
  alt: 'icon',
  newChip: false,
  icon: BlockOutlinedIcon,
  businessEnabled: false,
} as const;

// use AppRouter to add new routes
// featurePages are only for side bar navigation
const featurePages: FeaturePage[] = [
  {
    ...defaultPage,
    path: '/',
    nav: 'main',
    title: 'Dashboard',
    alt: 'easel showing abstract chart',
    icon: navIcons.navIconDashboard,
  },
  {
    ...defaultPage,
    path: '/investing',
    nav: 'main',
    title: 'Investments',
    alt: 'bank vault with open door',
    icon: navIcons.navIconInvestments,
    featureFlag: 'investmentsAcme',
  },
  {
    ...defaultPage,
    path: '/saved-reports',
    nav: 'main',
    title: 'Saved Reports',
    alt: 'various spending reports and charts',
    icon: navIcons.navIconReports,
    featureFlag: 'reportsRefactor',
  },
  {
    ...defaultPage,
    path: '/spending',
    nav: 'main',
    title: 'Spending',
    newChip: true,
    alt: 'Dashboard to view your spending habits',
    icon: navIcons.navIconSpending,
    featureFlag: 'spendingDashboard',
  },
  {
    ...defaultPage,
    path: '/net-worth',
    nav: 'main',
    title: 'Net Worth',
    alt: 'various spending reports and charts',
    icon: navIcons.navNetWorth,
    featureFlag: 'netWorthV2Feature',
  },
  {
    ...defaultPage,
    path: '/reports',
    nav: 'main',
    title: 'Reports',
    alt: 'various spending reports and charts',
    icon: navIcons.navIconReports,
    featureFlag: 'reports',
  },
  {
    ...defaultPage,
    path: '/transactions',
    nav: 'main',
    title: 'Transactions',
    alt: 'ledger of printed transactions',
    icon: navIcons.navIconTransactions,
  },
  {
    ...defaultPage,
    path: '/watchlist',
    nav: 'main',
    title: 'Watchlist',
    alt: 'bar graph with values',
    icon: navIcons.navIconWatchlist,
    featureFlag: 'spendingWatchList',
  },
  {
    ...defaultPage,
    path: '/goals',
    nav: 'main',
    title: 'Savings Goals',
    alt: 'bar graph with values',
    icon: navIcons.navIconSavingsGoals,
    featureFlag: 'goals',
  },
  {
    ...defaultPage,
    path: '/spending-plan',
    nav: 'main',
    title: 'Spending Plan',
    alt: 'spending plan icon',
    icon: navIcons.navIconSpendingPlan,
    featureFlag: 'spendingPlan',
  },
  {
    ...defaultPage,
    path: '/planning-tools',
    title: 'Planning Tools',
    alt: 'planning tools icon',
    icon: navIcons.navIconPlanningTools,
    featureFlag: 'planningTools',
  },
  {
    ...defaultPage,
    path: '/upcoming',
    nav: 'main',
    title: 'Bills & Income',
    alt: 'upcoming icon',
    icon: navIcons.navIconBillsAndPayments,
    featureFlag: 'upcoming',
  },
  {
    ...defaultPage,
    path: taxPlannerPath,
    title: 'Tax Planner',
    alt: 'Tax Planner',
    icon: navIcons.taxPlanner,
    featureFlag: 'taxPlanner',
  },
  {
    ...defaultPage,
    path: '/clients',
    nav: 'main',
    title: 'Clients & Projects',
    alt: 'clients icon',
    icon: navIcons.navIconClients,
    businessEnabled: true,
  },
  {
    ...defaultPage,
    path: '/sales-and-billing',
    nav: 'main',
    title: 'Sales & Billing',
    alt: 'sales and billing icon',
    icon: navIcons.navIconSalesAndBilling,
    businessEnabled: true,
    popover: {
      id: 'sales-and-billing-popover',
      default: 'Invoices',
      pages: [
        { label: 'Invoices', tabName: 'invoices', type: 'item' },
        { label: 'Estimates', tabName: 'estimates', type: 'item' },
        { label: 'Sales Receipts', tabName: 'sales-receipts', type: 'item' },
        { type: 'divider', label: '', tabName: '' },
        {
          label: 'Products & Services',
          tabName: 'products-and-services',
          type: 'item',
        },
      ],
    },
  },
  {
    ...defaultPage,
    path: '/settings',
    nav: 'main',
    title: 'Settings',
    alt: 'settings icon',
    icon: navIcons.navIconSettings,
  },
  {
    ...defaultPage,
    path: mileageTrackerPath,
    nav: 'main',
    title: 'Mileage',
    alt: 'mileage icon',
    icon: navIcons.navIconMileageIcon,
    businessEnabled: true,
  },
  {
    ...defaultPage,
    path: receiptCenterPath,
    nav: 'main',
    title: 'Expense Receipts',
    newChip: true,
    alt: 'expense receipts icon',
    icon: navIcons.navIconExpenseReceipts,
    accessSelector: receiptsSelectors.selectReceiptCenterEnabled,
  },
] as const;

export const getNavFromKey = (key: string) => featurePages.find((x) => x.path === `/${key}`);

export const makeNavigationItemId = (nav: FeaturePage) => {
  return `nav-menu-${nav.title || 'default'}`
    .toLowerCase()
    .replaceAll?.(' ', '-')
    .replace('/', '')
    .replace('&', '')
    .replaceAll?.('--', '-');
};
